/**
 * Личный фон чата (только у пользователя, в этом браузере).
 * Отдельные слоты для мобильного и десктопного интерфейса — картинка с телефона не подменяет картинку на ПК.
 * IndexedDB — без лимита localStorage ~5MB.
 */

const DB_NAME = 'messenger-chat-thread-user-bg';
const STORE = 'kv';
const DB_VER = 1;

/** @typedef {'mobile' | 'desktop'} ChatThreadBgSurface */

const KEY_MOBILE = 'userThreadBgDataUrl_mobile';
const KEY_DESKTOP = 'userThreadBgDataUrl_desktop';
/** Старый единый ключ — при первом чтении копируется в оба слота */
const LEGACY_KEY = 'userThreadBgDataUrl';

const MAX_DATA_URL_CHARS = 4_500_000;

function surfaceToKey(surface) {
  return surface === 'mobile' ? KEY_MOBILE : KEY_DESKTOP;
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB недоступен'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
  });
}

function dispatchUserBgChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('chat-thread-user-custom-bg-changed'));
  }
}

export function compressImageFileToDataUrl(file, maxSide = 1920, quality = 0.82) {
  return new Promise((resolve, reject) => {
    if (!file || !String(file.type || '').startsWith('image/')) {
      reject(new Error('Нужен файл изображения'));
      return;
    }
    const img = new Image();
    const blobUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(blobUrl);
      let w = img.naturalWidth || img.width;
      let h = img.naturalHeight || img.height;
      if (!w || !h) {
        reject(new Error('Некорректное изображение'));
        return;
      }
      const scale = Math.min(1, maxSide / Math.max(w, h));
      w = Math.max(1, Math.round(w * scale));
      h = Math.max(1, Math.round(h * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas недоступен'));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      let q = quality;
      let dataUrl = canvas.toDataURL('image/jpeg', q);
      while (dataUrl.length > MAX_DATA_URL_CHARS && q > 0.35) {
        q -= 0.08;
        dataUrl = canvas.toDataURL('image/jpeg', q);
      }
      if (dataUrl.length > MAX_DATA_URL_CHARS) {
        reject(new Error('Изображение слишком большое даже после сжатия'));
        return;
      }
      resolve(dataUrl);
    };
    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      reject(new Error('Не удалось прочитать файл'));
    };
    img.src = blobUrl;
  });
}

/**
 * @param {ChatThreadBgSurface} surface
 */
export async function getUserThreadBackgroundDataUrl(surface) {
  const key = surfaceToKey(surface);
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      tx.onerror = () => reject(tx.error);
      const r1 = store.get(key);
      r1.onsuccess = () => {
        const v = r1.result;
        if (typeof v === 'string' && v.startsWith('data:')) {
          resolve(v);
          return;
        }
        const r2 = store.get(LEGACY_KEY);
        r2.onsuccess = () => {
          const leg = r2.result;
          if (typeof leg === 'string' && leg.startsWith('data:')) {
            store.put(leg, KEY_MOBILE);
            store.put(leg, KEY_DESKTOP);
            store.delete(LEGACY_KEY);
            resolve(leg);
          } else {
            resolve('');
          }
        };
        r2.onerror = () => reject(r2.error);
      };
      r1.onerror = () => reject(r1.error);
    });
  } catch {
    return '';
  }
}

/**
 * @param {string} dataUrl
 * @param {ChatThreadBgSurface} surface
 */
export async function setUserThreadBackgroundDataUrl(dataUrl, surface) {
  const key = surfaceToKey(surface);
  const v = typeof dataUrl === 'string' && dataUrl.startsWith('data:') ? dataUrl : '';
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      if (v) store.put(v, key);
      else store.delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    if (v) throw e;
  }
  dispatchUserBgChanged();
}

/**
 * @param {ChatThreadBgSurface} surface
 */
export async function clearUserThreadBackgroundStorage(surface) {
  await setUserThreadBackgroundDataUrl('', surface);
}

export async function clearAllUserThreadBackgroundStorage() {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      store.delete(KEY_MOBILE);
      store.delete(KEY_DESKTOP);
      store.delete(LEGACY_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* ignore */
  }
  dispatchUserBgChanged();
}
